import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteEditBasic } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
  slug: string
  originalSlug: string
}

export default function updateClassWebsiteSlugAction(payload: Payload): PreparedAction {
  return {
    name: "UPDATE_CLASS_WEBSITE_SLUG",
    payload: payload,
    patches: null,
    // We don't allow undos because we actually change the slug on the server.
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let websiteTitle = models.classWebsite.attributes.title
  let newSlug = payload.slug
  let oldSlug = payload.originalSlug
  let context = assign({}, defaultClassWebsiteContext(payload, models), {
    websiteTitle,
    newSlug,
    oldSlug,
  })
  let newEvent: ClassWebsiteEditBasic = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: "slug",
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Changed URL for class website",
    titleForAnalytics: "Class Website - Changed Slug",
    activity: defaultClassWebsiteActivity(payload, models),
    description: `Changed the custom url for your "${websiteTitle}" class website from "${oldSlug}" to "${newSlug}"`,
    context,
    newAnalyticsEvents: [newEvent],
  }
}
